import { FC, useMemo, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import * as Icons from '@icons'

// COMPONENTS
import { Column } from '@components/templates/column'
import TextField from '@components/atoms/textfield'
import Button from '@components/atoms/button'
import Spinner from '@components/atoms/spinner'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { isEmail, isNonEmptyString } from '@utils/type-checkers'
import { useFixedSettings } from '@components/templates/fixed/_localStore/_hooks'
import { useInput } from '@hooks/inputs'


interface Props { }

const formId = 'add-info'

const AuthAddInfoScreen: FC<Props> = () => {
    const [searchParams] = useSearchParams()
    const fromParam = searchParams.get('from')
    const hasReferralPromo = !!(fromParam && fromParam.startsWith('/referral/'))

    const { isLoaded, user } = useUser()
    const { createToast } = useFixedSettings()
    const [isRedirecting, setIsRedirecting] = useState(false)

    const [firstName, firstNameProps] = useInput()
    const [lastName, lastNameProps] = useInput()
    const [email, emailProps] = useInput()

    const navigate = useNavigate()

    useEffect(() => {
        if (isLoaded && user && isNonEmptyString(user.firstName) && isNonEmptyString(user.lastName)) {
            navigate(fromParam || '/sign-up/billing', { replace: true })
        }
    }, [isLoaded, user, navigate, fromParam])

    const ref = useRef<HTMLDialogElement>(null)
    useEffect(() => {
        if (ref.current) {
            if (isRedirecting) ref.current.showModal()
            else ref.current.close()
        }
    }, [isRedirecting])

    const submittable = useMemo(() => isNonEmptyString(firstName) && isNonEmptyString(lastName) && isNonEmptyString(email) && isEmail(email), [firstName, lastName, email])

    async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        try {
            event.preventDefault()
            if (isLoaded && user?.update != null) {
                await user.update({ firstName, lastName })

                // Create the email adderss and start the verification process. Just sends out
                // the verifiction email.
                try {
                    const emailResult = await user.createEmailAddress({ email })

                    const baseUrl = import.meta.env.VITE_VIRTUAL_CARD_BASE_URL.replace(".api", "").replace("api", "")
                    const redirectUrl = `${baseUrl}/account/profile/email/${emailResult.id}/verify`

                    await emailResult.prepareVerification({
                        strategy: 'email_link',
                        redirectUrl
                    })

                } catch (error) {
                    console.log("email error:", error)
                }
                if (fromParam) {
                    setIsRedirecting(true)

                    // Reload user data and wait 1 second to ensure fresh data
                    setTimeout(async () => {
                        try {
                            await user.reload()
                        } catch (error) {
                            console.error('Failed to reload user:', error)
                        }
                        navigate(fromParam)
                    }, 1000)
                } else {
                    navigate('/sign-up/billing')
                }
            } else throw Object.assign(new Error('Auth Services have yet to load.'), { errors: [{ longMessage: 'Auth Services have yet to load.' }] })
        } catch (error: any) {
            console.log(error)
            const defaultMsg = "An issue occured while trying to update your user profile."
            createToast({
                type: 'error',
                text: ('errors' in error && Array.isArray(error.errors))
                    ? error.errors[0]?.longMessage || defaultMsg
                    : defaultMsg
            })
        }
    }

    return (<>
        <Column.Content style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                <h1 className='text-center text-blue-500'>Welcome!</h1>
                <h2 className='text-center'>Create Your Account</h2>
                {hasReferralPromo && (
                    <div style={{
                        background: '#ecfdf5',
                        border: '1px solid #10b981',
                        borderRadius: '1rem',
                        padding: '1rem',
                        marginTop: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem'
                    }}>
                        <div style={{
                            animation: 'bounce 1s ease-out 2',
                            animationDelay: '0.3s',
                            background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                            borderRadius: '0.75rem',
                            height: '3rem',
                            width: '3rem',
                            minWidth: '3rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white'
                        }}>
                            <Icons.GiftBox width="1.5rem" height="1.5rem" tint="white" />
                        </div>
                        <div>
                            <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '0.9375rem', fontWeight: '600', color: '#064e3b' }}>Referral Promotion</h4>
                            <p style={{ margin: 0, fontSize: '0.8125rem', lineHeight: '1.4', color: '#065f46' }}>
                                You are eligible for the referral. Your credits will be applied once the steps are completed.
                            </p>
                        </div>
                    </div>
                )}
            </div>
            <div style={{ flex: 1 }} />
            <form id={formId} onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
                <TextField
                    {...firstNameProps}
                    name="given-name"
                    placeholder="First Name"
                    autoComplete="given-name"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault()
                            const lastNameInput = document.querySelector('input[name="family-name"]') as HTMLInputElement
                            lastNameInput?.focus()
                        }
                    }}
                />
                <TextField
                    {...lastNameProps}
                    name="family-name"
                    placeholder="Last Name"
                    autoComplete="family-name"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault()
                            const emailInput = document.querySelector('input[name="email"]') as HTMLInputElement
                            emailInput?.focus()
                        }
                    }}
                />
                <TextField {...emailProps} type="email" name="email" placeholder="E-mail Address" autoComplete="email" required />
            </form>
            <div style={{ flex: 2 }} />
        </Column.Content>
        <Column.Footer>
            <Button
                form={formId}
                type="submit"
                disabled={!submittable}
            >
                Continue
            </Button>
        </Column.Footer>
        <dialog ref={ref}>
            <div>
                <div className="flex flex-col items-center justify-center" style={{ background: 'var(--splitcard-white)', gap: '1rem', padding: '1rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                    <Spinner />
                </div>
            </div>
        </dialog>
    </>)
}

const AddInfoRoute: RouteObject = {
    path: "add-info",
    element: <AuthAddInfoScreen />,
}

export default AddInfoRoute
